import { useRef, useState } from 'react';
import { fn } from 'storybook/test';

import type { ChangeEvent } from 'react';
import type { Meta, StoryFn } from '@storybook/react';
import type { InputFieldProps } from './InputField';

import { InputField } from './InputField';

import { Container } from 'storyComponents/Container';
import { Icon } from 'components';

const codeString = `
// Has all HTMLInputElement props available except "className" and "style" 
<InputField aria-labelledby={aria-labelledby} id={id} onChange={onChangeFunction}
  value={value} type={type} clearTrigger={clearTrigger} />
`;

const transformArgs = (args: InputFieldProps) => {
  // args.variant is provided as an array because the control type is set to a checkbox. So this transforms the selected value into its proper value: a string
  const variantArray = (args.variant as unknown as string[]) ?? [];
  return { ...args, ...{ variant: variantArray.toString() } } as InputFieldProps;
};

// eslint-disable-next-line import/no-default-export
export default {
  title: 'Atoms/InputField',
  component: InputField,
  argTypes: {
    variant: {
      options: ['large'],
      control: 'check',
    },
    width: {
      control: 'radio',
      options: [undefined, 'small', 'compact'],
    },
    type: {
      control: 'radio',
      options: ['text', 'password', 'email'],
    },
    background: {
      control: 'check',
      options: ['white'],
    },
    id: {
      control: 'text',
    },
    autoComplete: {
      table: {
        disable: true,
      },
    },
  },
  parameters: {
    inspectComponent: InputField,
    codeString: codeString,
  },
} as Meta<InputFieldProps>;

const Template: StoryFn<InputFieldProps> = (args) => {
  const [inputValue, setInputValue] = useState<InputFieldProps['value']>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    fn()('handleChange');
  };

  const handleClear = () => {
    setInputValue('');
    fn()('handleClear');
  };

  return (
    <Container>
      <InputField
        {...transformArgs(args)}
        value={inputValue}
        onChange={handleChange}
        onClear={handleClear}
        ref={inputRef}
      />
    </Container>
  );
};

const defaultArgs: Partial<InputFieldProps> = {
  type: 'text',
  variant: undefined,
  id: '4f9ca77d',
};

export const InputFieldExample = Template.bind({});
InputFieldExample.args = {
  ...defaultArgs,
};

export const WithStringClearTrigger = Template.bind({});
WithStringClearTrigger.args = {
  ...defaultArgs,
  clearTrigger: 'Clear',
};

export const WithIconClearTrigger = Template.bind({});
WithIconClearTrigger.args = {
  ...defaultArgs,
  clearTrigger: <Icon name="Cross" />,
};
